'use memo';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { FormItem } from '@/components/FormItem';
import Input from '@/components/Input/Input';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import NavigationCard, { type NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { SettingsItemCard, SettingsItemDescription, SettingsItemLeftColumn, SettingsItemRightColumn, SettingsItemRow } from '../../../../../-components/SettingsItem';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/basic-settings/')({
  component: OutboundBasicSettingsPage
});

/**
 * OUT 基本設定。表示名（name）を編集できる。データ種別は作成後 immutable のため表示のみ。
 * 運用モード・起動モード・スケジュールは「実行計画」タブの責務（IN 同型）。
 */
function OutboundBasicSettingsPage() {
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline'
  });
  const {
    toast
  } = useToast();
  const {
    id: pipelineId
  } = Route.useParams();
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const basicInfo = detailQuery.data?.basicInfo;
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');

  // 編集開始時にサーバ値で編集 state をシードする（useEffect での同期 setState は避ける）。
  const handleStartEditing = () => {
    if (basicInfo == null) return;
    setName(basicInfo.name);
    setIsEditing(true);
  };
  const updateMutation = useMutation(orpc.outboundDataPipeline.updateBasicInfo.mutationOptions({
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.outboundDataPipeline.get.key({
          input: {
            id: pipelineId
          }
        })
      });
      setIsEditing(false);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  }));
  const handleSave = () => {
    updateMutation.mutate({
      id: pipelineId,
      name: name.trim()
    });
  };
  const navigationSections: NavigationSectionGroup[] = [{
    titleLabel: t('detail.basicSettings.title'),
    to: '/settings/data-connector/outbound/data-pipeline/$id/basic-settings',
    hash: 'basic-info'
  }];
  return <FillScreen>
      <HStack className="gap-0 px-12 py-6">
        <div className="z-10 sticky top-6 w-52 shrink-0">
          <NavigationCard sections={navigationSections} />
        </div>

        <VStack className="relative z-0 min-w-0 flex-1 gap-6">
          <SettingsItemRow id="basic-info">
            <SettingsItemLeftColumn>
              <Text variant="h3">{t('detail.basicSettings.title')}</Text>
              <SettingsItemDescription>
                {t('detail.basicSettings.description')}
              </SettingsItemDescription>
            </SettingsItemLeftColumn>

            <SettingsItemRightColumn>
              <SettingsItemCard className="relative overflow-visible">
                {basicInfo == null ? <Text variant="p" className="text-neutral-muted-on-fill">
                    {t('loading')}
                  </Text> : isEditing ? <VStack className="gap-5">
                    <FormItem fieldTitle={t('name')} fieldInput={<Input value={name} width={320} placeholder={t('namePlaceholder')} onChange={e => setName(e.target.value)} />} />
                    <FormItem fieldTitle={t('dataType')} fieldInput={<Text variant="p">{basicInfo.dataTypeLabel}</Text>} />
                    <HStack className="justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsEditing(false)} disabled={updateMutation.isPending}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={handleSave} disabled={updateMutation.isPending || name.trim() === ''}>
                        {t('save')}
                      </Button>
                    </HStack>
                  </VStack> : <>
                    <Button variant="secondary" className="absolute top-6 right-6 z-10" onClick={handleStartEditing}>
                      {t('edit')}
                    </Button>
                    <VStack className="gap-5">
                      <ReadField label={t('name')} value={basicInfo.name} />
                      <ReadField label={t('dataType')} value={basicInfo.dataTypeLabel} />
                    </VStack>
                  </>}
              </SettingsItemCard>
            </SettingsItemRightColumn>
          </SettingsItemRow>
        </VStack>
      </HStack>
    </FillScreen>;
}
function ReadField({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <VStack className="gap-1">
      <Text variant="labelSmall" className="text-neutral-muted-on-fill">
        {label}
      </Text>
      <Text variant="p">{value}</Text>
    </VStack>;
}